import type { ParamValue, TemplateId } from "../../lib/types";
import { TEMPLATES, TEMPLATE_IDS, resolveParams, type ParamDef } from "../../lib/furniture/templates";
import { NumberStepper } from "../ui/NumberStepper";
import { cn } from "../../lib/cn";

interface Props {
  template: TemplateId;
  /** Stored (possibly partial) params; defaults are merged for display. */
  params: Record<string, ParamValue> | undefined;
  onTemplateChange: (next: TemplateId) => void;
  onParamChange: (id: string, value: ParamValue, resolved: Record<string, ParamValue>) => void;
  /** Header slot rendered left of the template select (e.g. "Shape" label). */
  header?: React.ReactNode;
  /** Test id for the template `<select>`. */
  selectTestId?: string;
  className?: string;
  /** Passed through to integer steppers. Import preview edits are not room history. */
  history?: "design" | "off";
}

/**
 * Schema-driven editor for a parametric template: a type `<select>` plus
 * one control per visible param (segmented chips for enums, steppers for
 * ints, switches for bools). Shared by the inspector's Shape section and
 * the import preview so both surfaces edit templates identically.
 */
export function TemplateParamsEditor({
  template: templateId,
  params: stored,
  onTemplateChange,
  onParamChange,
  header,
  selectTestId = "furniture-template-select",
  className,
  history = "design",
}: Props) {
  const template = TEMPLATES[templateId];
  const params = resolveParams(template, stored);
  const visible = template.params.filter((def) => {
    if (def.hidden) return false;
    if (!def.visibleWhen) return true;
    return params[def.visibleWhen.param] === def.visibleWhen.equals;
  });

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between">
        {header}
        <label className="flex items-center gap-1.5 text-caption text-ink-500">
          <span className="sr-only">Type</span>
          <select
            value={templateId}
            onChange={(e) => onTemplateChange(e.target.value as TemplateId)}
            data-testid={selectTestId}
            className="h-7 rounded-lg bg-white px-2 text-caption font-medium text-ink-700 ring-1 ring-ink-200 hover:ring-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
          >
            {TEMPLATE_IDS.map((id) => (
              <option key={id} value={id}>
                {TEMPLATES[id].label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {visible.length === 0 ? (
        <p className="text-caption text-ink-400">This shape has no extra options.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map((def) => (
            <ParamControl
              key={def.id}
              def={def}
              value={params[def.id]}
              onChange={(v) => onParamChange(def.id, v, { ...params, [def.id]: v })}
              history={history}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ParamControl({
  def,
  value,
  onChange,
  history = "design",
}: {
  def: ParamDef;
  value: ParamValue;
  onChange: (v: ParamValue) => void;
  history?: "design" | "off";
}) {
  if (def.type === "int") {
    return (
      <NumberStepper
        label={def.label}
        value={typeof value === "number" ? value : def.default}
        onChange={(v) => onChange(Math.min(def.max, Math.max(def.min, Math.round(v))))}
        history={history}
        min={def.min}
        max={def.max}
        step={def.step ?? 1}
      />
    );
  }
  if (def.type === "bool") {
    const on = value === true;
    return (
      <label className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-ink-500">{def.label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={def.label}
          onClick={() => onChange(!on)}
          className={cn(
            "relative h-6 w-11 rounded-full transition-colors",
            on ? "bg-ink-900" : "bg-ink-200"
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform",
              on ? "translate-x-[22px]" : "translate-x-0.5"
            )}
          />
        </button>
      </label>
    );
  }
  // enum
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-500">{def.label}</span>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={def.label}>
        {def.options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(o.value)}
              className={cn(
                "h-8 rounded-full px-3 text-xs font-medium transition-colors",
                active
                  ? "bg-ink-900 text-white"
                  : "bg-ink-50 text-ink-600 ring-1 ring-ink-100 hover:bg-ink-100"
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
